/**
 * Ties the top loading bar to actual network activity.
 *
 * nextjs-toploader finishes the bar at URL-change time, before the new page
 * has fetched any data. These axios interceptors keep the bar honest: it
 * starts when requests are in flight, advances as each one completes, and
 * finishes only when the network goes idle. Both this module and
 * nextjs-toploader drive the same NProgress singleton, so during navigation
 * the toploader starts the bar and the in-flight requests carry it through
 * to the real finish.
 */

import axios from "axios";
import NProgress from "nprogress";

// Delay before showing the bar, so snappy requests don't flash it.
const SHOW_DELAY_MS = 100;

let pending = 0;
let showTimer: ReturnType<typeof setTimeout> | null = null;

function requestStarted() {
  pending += 1;
  if (pending === 1 && showTimer === null && !NProgress.isStarted()) {
    showTimer = setTimeout(() => {
      showTimer = null;
      if (pending > 0 && !NProgress.isStarted()) NProgress.start();
    }, SHOW_DELAY_MS);
  }
}

function requestFinished() {
  pending = Math.max(0, pending - 1);
  if (pending === 0) {
    if (showTimer) {
      clearTimeout(showTimer);
      showTimer = null;
    }
    if (NProgress.isStarted()) NProgress.done();
  } else if (NProgress.isStarted()) {
    NProgress.inc();
  }
}

let installed = false;

export function installRequestProgress() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  axios.interceptors.request.use((config) => {
    requestStarted();
    return config;
  });
  axios.interceptors.response.use(
    (response) => {
      requestFinished();
      return response;
    },
    (error) => {
      requestFinished();
      return Promise.reject(error);
    }
  );
}
